/**
 * Keeps the nightlife-api cookie session alive.
 *
 * Access tokens last 15 minutes. The refresh token lasts 60 days and the only
 * way to trade it for a new access token is `refreshSession`. This app is a
 * WEB client: both tokens are httpOnly cookies on the API host, so the
 * mutation is sent with `client_type: WEB` and `credentials: "include"`.
 * A successful WEB refresh returns `tokens: null` — the Set-Cookie headers
 * are the result.
 *
 * `refreshSession` shares the API's auth rate limit (10 per 15 minutes per
 * IP, same bucket as login). This module therefore:
 *   - does not call it for a visitor with no stored user (`me` is public and
 *     returns null both logged-out and when the access token expired)
 *   - renews on a 12 minute cadence, not on every tab focus
 *   - treats transport failures, 5xx, and 429 as unavailable, not as logout
 */

const GRAPHQL_URL =
  process.env.REACT_APP_CMS_GRAPHQL_URL ||
  "https://api-dev.nightlife.net/graphql";

const STORAGE_KEY_USER = "nightlife_reporting_user";
const FRESH_KEY = "nightlife_session_fresh_at";

/** Access token is 15 min, so renew at 12 and leave headroom for a slow network. */
const REFRESH_EVERY_MS = 12 * 60 * 1000;
/** The stamp is written after `me` returns, which is after the timer started. */
const DUE_SLACK_MS = 30 * 1000;

/** A blip can be retried. A 429 must not be: it shares login's 15 minute bucket. */
const MIN_RETRY_GAP_MS = 30 * 1000;
const RATE_LIMIT_BACKOFF_MS = 15 * 60 * 1000;

const REFRESH_SESSION_QUERY = `
  mutation RefreshSession($input: RefreshSessionInput!) {
    refreshSession(input: $input) {
      tokens {
        access_token
      }
      errors {
        message
        code
      }
    }
  }
`;

export type RefreshResult = "refreshed" | "expired" | "unavailable";

type RefreshCall = { result: RefreshResult; status: number };

let inFlight: Promise<RefreshResult> | null = null;
let blockedUntil = 0;
let lastResult: RefreshResult = "unavailable";
let unavailableStreak = 0;
let lastFreshAt = 0;
let bootstrapped = false;
let timer: ReturnType<typeof setInterval> | null = null;

export function hasStoredSession(): boolean {
  try {
    return !!localStorage.getItem(STORAGE_KEY_USER);
  } catch {
    return false;
  }
}

/**
 * Stamp a token that was just issued (login or refresh). `me` succeeding does
 * not issue a token, so a later `me` must not move this forward or tab focus
 * would postpone renewal past the 15 minute expiry.
 */
export function noteSessionFresh(at = Date.now()): void {
  lastFreshAt = at;
  try {
    sessionStorage.setItem(FRESH_KEY, String(at));
  } catch {
    // sessionStorage can throw in private mode; the in-memory stamp still works.
  }
}

function lastFresh(): number {
  if (lastFreshAt) return lastFreshAt;
  try {
    lastFreshAt = Number(sessionStorage.getItem(FRESH_KEY)) || 0;
  } catch {
    lastFreshAt = 0;
  }
  return lastFreshAt;
}

/** First observation in this tab only. Does not extend an existing stamp. */
export function noteSessionObserved(): void {
  if (lastFresh()) return;
  noteSessionFresh();
}

function isAccessTokenExpired(json: any): boolean {
  const errors = json?.errors;
  if (!Array.isArray(errors)) return false;
  return errors.some(
    (err) =>
      err?.extensions?.code === "ACCESS_TOKEN_EXPIRED" ||
      /session needs to be refreshed/i.test(err?.message || ""),
  );
}

function isDeadSession(errors: any[]): boolean {
  return errors.some((err) => err?.code === "SESSION_EXPIRED");
}

async function callRefresh(): Promise<RefreshCall> {
  try {
    const res = await fetch(GRAPHQL_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "apollo-require-preflight": "true",
      },
      credentials: "include",
      cache: "no-store",
      body: JSON.stringify({
        operationName: "RefreshSession",
        query: REFRESH_SESSION_QUERY,
        variables: { input: { client_type: "WEB" } },
      }),
    });
    if (!res.ok) return { result: "unavailable", status: res.status };
    const json = await res.json();
    if (Array.isArray(json?.errors) && json.errors.length) {
      return { result: "unavailable", status: res.status };
    }
    const payload = json?.data?.refreshSession;
    if (!payload) return { result: "unavailable", status: res.status };
    if (Array.isArray(payload.errors) && payload.errors.length) {
      return {
        result: isDeadSession(payload.errors) ? "expired" : "unavailable",
        status: res.status,
      };
    }
    return { result: "refreshed", status: res.status };
  } catch {
    return { result: "unavailable", status: 0 };
  }
}

/**
 * Refresh now. No stored user and no `sessionKnown` (the server already
 * reported ACCESS_TOKEN_EXPIRED) means there is nothing to renew.
 */
export function ensureSessionRefreshed(options?: {
  sessionKnown?: boolean;
}): Promise<RefreshResult> {
  if (typeof window === "undefined") return Promise.resolve("unavailable");
  if (!options?.sessionKnown && !hasStoredSession())
    return Promise.resolve("unavailable");
  if (inFlight) return inFlight;

  const now = Date.now();
  if (now < blockedUntil) return Promise.resolve(lastResult);

  inFlight = callRefresh()
    .then((outcome) => {
      lastResult = outcome.result;
      if (outcome.result === "refreshed") {
        unavailableStreak = 0;
        noteSessionFresh();
      } else if (outcome.result === "unavailable") {
        unavailableStreak += 1;
      }
      // 429, a dead refresh token, and a second blip in a row stop for the
      // whole auth-limit window. One blip can be retried once, after 30s.
      const stopForWindow =
        outcome.status === 429 ||
        outcome.result === "expired" ||
        unavailableStreak >= 2;
      blockedUntil =
        Date.now() + (stopForWindow ? RATE_LIMIT_BACKOFF_MS : MIN_RETRY_GAP_MS);
      return outcome.result;
    })
    .finally(() => {
      inFlight = null;
    });
  return inFlight;
}

/**
 * Proactive renewal. Skips the network unless the last proof of a live token
 * is at least 12 minutes old, so tab focus does not burn the auth rate limit.
 */
export function refreshSessionIfDue(): Promise<RefreshResult> {
  if (!hasStoredSession()) return Promise.resolve("unavailable");
  const fresh = lastFresh();
  if (!fresh || Date.now() - fresh + DUE_SLACK_MS < REFRESH_EVERY_MS) {
    return Promise.resolve(fresh ? "refreshed" : "unavailable");
  }
  return ensureSessionRefreshed();
}

/** One check per page load. A reload inside the 12 minute window does not refresh. */
export function bootstrapSessionOnce(): void {
  if (bootstrapped || typeof window === "undefined") return;
  bootstrapped = true;
  void refreshSessionIfDue();
}

/**
 * Renew ahead of expiry. A hidden tab's timers are frozen, so becoming
 * visible refreshes only when the 12 minute window has already elapsed.
 */
export function startProactiveSessionRefresh(): () => void {
  if (typeof window === "undefined") return () => {};

  const onVisible = () => {
    if (document.visibilityState === "visible") void refreshSessionIfDue();
  };
  const onOnline = () => {
    void refreshSessionIfDue();
  };

  if (timer) clearInterval(timer);
  timer = setInterval(() => {
    void refreshSessionIfDue();
  }, REFRESH_EVERY_MS);
  document.addEventListener("visibilitychange", onVisible);
  window.addEventListener("online", onOnline);

  return () => {
    if (timer) clearInterval(timer);
    timer = null;
    document.removeEventListener("visibilitychange", onVisible);
    window.removeEventListener("online", onOnline);
  };
}

/** Forget the issued-at stamp. Does not clear a 429 or dead-session backoff. */
export function clearSessionFreshness(): void {
  lastFreshAt = 0;
  try {
    sessionStorage.removeItem(FRESH_KEY);
  } catch {
    // ignore
  }
}

/** Drop backoff and the freshness stamp. A new login calls this. */
export function resetSessionRefreshBackoff(): void {
  blockedUntil = 0;
  lastResult = "unavailable";
  unavailableStreak = 0;
  clearSessionFreshness();
}

/** Clears the page-load guard and any timer the tests started. */
export function resetSessionRefreshForTests(): void {
  resetSessionRefreshBackoff();
  bootstrapped = false;
  inFlight = null;
  if (timer) clearInterval(timer);
  timer = null;
}

/**
 * GraphQL POST with credentials. On ACCESS_TOKEN_EXPIRED, refresh once and
 * replay. A failed refresh returns the original response; it does not mean
 * the session is dead.
 */
export async function graphqlFetch(
  body: Record<string, unknown>,
): Promise<Response> {
  const exec = () =>
    fetch(GRAPHQL_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "apollo-require-preflight": "true",
      },
      credentials: "include",
      cache: "no-store",
      body: JSON.stringify(body),
    });

  const response = await exec();
  let json: any = null;
  try {
    json = await response.clone().json();
  } catch {
    return response;
  }
  if (!isAccessTokenExpired(json)) return response;
  const refreshed = await ensureSessionRefreshed({ sessionKnown: true });
  if (refreshed !== "refreshed") return response;
  return exec();
}
